import { Component, Input, OnInit } from '@angular/core';
// eslint-disable-next-line no-restricted-imports
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { Papa } from 'ngx-papaparse';
import { takeUntil, tap } from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';

import { fadeInEnter } from '@libs/src/animations/animations';
import {
  csvTemplateKeysMap,
  DownloadCsvTemplateType,
  mapCsvTemplateKeys,
} from '@main-client/src/app/download-csv-template/download-csv-template.helper';
import {
  TemplateGuide,
  UploadCSVListDrawerData,
  UploadItem,
} from '@main-client/src/app/upload-csv-list-drawer/upload-csv-list-drawer';

import chunk from 'lodash-es/chunk';
import isEmpty from 'lodash-es/isEmpty';
import keys from 'lodash-es/keys';
import pick from 'lodash-es/pick';
import pickBy from 'lodash-es/pickBy';
import values from 'lodash-es/values';

@Component({
  animations: [fadeInEnter],
  providers: [DestroySubscriptionsService],
  selector: 'app-upload-csv-list-drawer',
  templateUrl: './upload-csv-list-drawer.component.html',
})
export class UploadCsvListDrawerComponent implements OnInit {
  @Input() data: UploadCSVListDrawerData;
  viewMode = 'upload';
  items: UploadItem[] = [];
  chunkedItems: UploadItem[][] = [];
  isLoading = false;
  cta = '';
  description = '';
  title = '';
  templateType: DownloadCsvTemplateType;
  csvHeaders: string[] = [];
  tableColumns = ['item'];
  csvMappedHeaders: string[] = [];
  pageSize = 1;
  currentPage = 0;
  totalCount = 0;
  dataSource: MatTableDataSource<UploadItem>;
  templateTitle: string;
  templateData: string[][];
  customText: TemplateGuide;

  constructor(
    private readonly destroy: DestroySubscriptionsService,
    private readonly drawerRef: NzDrawerRef,
    private readonly papa: Papa,
  ) {}

  ngOnInit() {
    this.cta = this.data.cta;
    this.templateType = this.data.templateType;
    this.templateData = this.data.templateData;
    this.templateTitle = this.data.templateTitle;
    this.csvHeaders = keys((csvTemplateKeysMap as any)[this.templateType]);
    this.csvMappedHeaders = values(
      csvTemplateKeysMap[this.templateType as keyof typeof csvTemplateKeysMap],
    );
    this.customText = this.data.customText;
  }

  onAfterAddingFile(csvFile: File) {
    this.isLoading = true;
    this.papa.parse(csvFile, {
      complete: (result) => {
        const items = result.data.filter(
          (item: any) => !isEmpty(pickBy(pick(item, this.csvHeaders))),
        );
        this.viewMode = 'review';
        this.isLoading = false;
        if (isEmpty(items)) {
          this.description = $localize`:@@global.emptyCsvUploadTitle:The CSV file you uploaded is empty.`;
          return;
        }
        this.items = mapCsvTemplateKeys(items, this.templateType);
        this.description = this.data.description(this.items.length);
        this.chunkedItems = chunk(this.items, this.pageSize);
        this.dataSource = new MatTableDataSource<UploadItem>(
          this.chunkedItems[this.currentPage],
        );
        this.totalCount = this.items.length;
      },
      header: true,
    });
  }

  ctaClicked(items: UploadItem[] = []) {
    this.isLoading = true;
    this.data
      .submit(items.map((item) => item.value))
      .pipe(
        takeUntil(this.destroy),
        tap((results: UploadCSVListDrawerData) => {
          this.items = results.items ?? [];
          this.currentPage = 0;
          this.totalCount = this.items.length;
          this.description = results.description();
          this.chunkedItems = chunk(results.items, this.pageSize);
          this.dataSource = new MatTableDataSource<UploadItem>(
            this.chunkedItems[this.currentPage],
          );
          this.viewMode = 'results';
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  onPageChange(pageEvent: PageEvent) {
    if (this.isLoading) {
      return;
    }
    this.currentPage = pageEvent.pageIndex;
    this.dataSource = new MatTableDataSource<UploadItem>(
      this.chunkedItems[this.currentPage],
    );
  }

  closeDrawer(): void {
    this.drawerRef.close();
  }
}
